import { IDatasourceSQLMap } from 'src/app/utils/datasource-sql-map/datasource';
import { DatasourceSpecificColumnsSQLStatementsCompiler } from 'src/app/utils/datasource-sql-map/datasource-specific-sql-statements-compiler';
import { multipleSelectComponentCompileSQLTokens } from 'src/app/utils/datasource-sql-map/utils';
import { BiVizDashboardResourcesSqlMap } from './biviz-dashboard-resources-datasource-sql-map';

/**
 * SQL map for the `dashboard_deco_activity` table (decoration inflow / new placed decos).
 *
 * The table mirrors `dashboard_resources` (same `player_day` / `player_dim` structs), so all the
 * generic cohort/activity filters are reused verbatim by spreading the resources columns. Only the
 * deco-specific columns differ in their physical paths:
 *  - `source` / `income_type` are top-level columns,
 *  - the deco_context dimensions live under `deco_context.*` here (vs `source_params.ctx_deco_*` on
 *    `dashboard_resources`).
 */
export class BiVizDashboardDecoActivitySqlMap {
    public static sqlMap: IDatasourceSQLMap = {
        dataset: 'bi_viz',
        tableId: 'dashboard_deco_activity',
        columns: {
            // reuse all generic player_day / player_dim columns from the resources map
            ...BiVizDashboardResourcesSqlMap.sqlMap.columns,
            // source is the same physical column as in resources
            RESOURCES_SOURCE: {
                columnId: 'RESOURCES_SOURCE',
                columnSQL: 'source',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'source',
                        state
                    );
                },
            },
            // payment type maps to `income_type` on this table
            RESOURCE_PAYMENT_TYPE: {
                columnId: 'RESOURCE_PAYMENT_TYPE',
                columnSQL: 'income_type',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'income_type',
                        state
                    );
                },
            },
            // deco_context dimensions live under `deco_context.*` here
            DECO_CONTEXT_RARITY: {
                columnId: 'DECO_CONTEXT_RARITY',
                columnSQL: 'deco_context.rarity',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'deco_context.rarity',
                        state
                    );
                },
            },
            DECO_CONTEXT_SIZE: {
                columnId: 'DECO_CONTEXT_SIZE',
                columnSQL: 'deco_context.size',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'deco_context.size',
                        state
                    );
                },
            },
            DECO_CONTEXT_SET_ID: {
                columnId: 'DECO_CONTEXT_SET_ID',
                columnSQL: 'deco_context.set_id',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'deco_context.set_id',
                        state
                    );
                },
            },
            DECO_CONTEXT_INSPIRATIONS: {
                columnId: 'DECO_CONTEXT_INSPIRATIONS',
                columnSQL: 'deco_context.inspirations',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'deco_context.inspirations',
                        state
                    );
                },
            },
        },
    };

    public static get sqlStatementsCompiler() {
        return new DatasourceSpecificColumnsSQLStatementsCompiler({
            sqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
            specificColumns: [
                'RESOURCES_SOURCE',
                'RESOURCE_PAYMENT_TYPE',
                'DECO_CONTEXT_RARITY',
                'DECO_CONTEXT_SIZE',
                'DECO_CONTEXT_SET_ID',
                'DECO_CONTEXT_INSPIRATIONS',
            ],
            datasourcePrefix: 'deco',
        });
    }

    public static readonly ALL_DECO_METRICS = [
        'decoration_inflow',
        'new_decos_placed',
    ];
}

